import {
  Tag as BaseTag,
  ITagProps,
  colors,
  Icon,
  IIconProps,
  IBadgeProps,
  Badge,
} from 'common-components';
import { TCertificationState, TProjectState, TUserCertificationState } from '@definitions';
import React from 'react';
import { css } from '@emotion/react';

type TPresetType =
  | 'certification-state'
  | 'user-certification-state'
  | 'question-state'
  | 'project-state'
  | 'common-state'
  | 'workflow-type'
  | 'user-status'
  | 'user-type'
  | 'task-state'
  | 'hit-state'
  | 'segment-type'
  | 'form-state';

type TPresetStateType = TCertificationState | TUserCertificationState | TProjectState | string;

type IPresetTagProps = Omit<ITagProps, 'icon'> & {
  preset: TPresetType
  state?: TPresetStateType
  withDot?: boolean
  dotProps?: IBadgeProps
  icon?: IIconProps['icon']
};
export const PresetTag = function ({
  preset,
  state,
  withDot,
  icon,
  children,
  style,
  ...rest
}: IPresetTagProps) {
  const color = getPreset(preset, state);
  const getPrefix = () => {
    if (withDot && !icon) {
      return (
        <Badge
          dot
          color={color}
          css={css`
            align-self: center;
            margin-right: 5px;
            font-size: 12px;
          `}
        />
      );
    }
    if (icon && !withDot) {
      return <Icon icon={icon} color={color} size={12} style={{ marginRight: 6 }} />;
    }
  };

  return (
    <BaseTag color={color} style={{ fontWeight: 500, ...style }} {...rest}>
      {getPrefix()}
      {children}
    </BaseTag>
  );
};

const getPreset = (
  preset: IPresetTagProps['preset'],
  state?: IPresetTagProps['state'] | string,
) => {
  switch (preset) {
    case 'certification-state':
      return getCertificationStateColor(state as TCertificationState);

    case 'user-certification-state':
      return getUserCertificationStateColor(state as TUserCertificationState);

    case 'project-state':
      return getProjectStateColor(state as TProjectState);

    case 'common-state':
      return getCommonStateColor(state as string);
  }
};

const getCommonStateColor = (state: string) => {
  switch (state) {
    case 'darkGray':
      return colors.gray700;
    case 'darkGreen':
      return colors.success700;
    default:
      return colors.gray700;
  }
};

const getCertificationStateColor = (state?: TCertificationState) => {
  switch (state?.toString()) {
    case '1':
      return colors.gray900;
    case '10':
      return colors.success600;
    case '20':
      return colors.warning600;
    case '100':
      return colors.gray600;
    default:
      return colors.gray600;
  }
};

const getUserCertificationStateColor = (state?: TUserCertificationState) => {
  switch (state?.toString()) {
    case '0':
      return colors.error600;
    case '10':
      return colors.warning600;
    case '20':
      return colors.success600;
    case '30':
      return colors.blue600;
    default:
      return colors.gray600;
  }
};

const getProjectStateColor = (state?: TProjectState) => {
  switch (state?.toString()) {
    case '1':
    case '2':
    case '3':
    case '4':
      return colors.gray700;
    case '10':
      return colors.success700;
    case '15':
      return colors.blue700;
    case '20':
      return colors.primary700;
    case '30':
      return colors.gray700;
    case '40':
      return colors.gray700;
    case '50':
      return colors.gray700;
    default:
      return colors.success700;
  }
};
